import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Fuel } from 'lucide-react';

import { supplyApi } from '../../../lib/supplyApi';
import { formatLitres, formatNombre } from '../../../lib/formats';
import { libelle } from '../../../lib/libelles';
import { Aide } from '../../ui/aide';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { BandeauFile } from '../../business/BandeauFile';

/**
 * Stations à réapprovisionner (lot 032) : nos stations, la plus pressée
 * d'abord — autonomie des cuves (jours de vente que couvre le stock),
 * commandes de réapprovisionnement en cours, réceptions attendues.
 *
 * Sur la répartition, le panneau ne s'affiche que si la société a des
 * stations ; sur l'écran Réapprovisionnement, il montre la liste entière.
 */

const jours = (valeur) => (valeur === null || valeur === undefined ? '—' : `${formatNombre(valeur, 1)} j`);

export const useStationsAReapprovisionner = () => {
  const [stations, setStations] = useState([]);
  const [chargement, setChargement] = useState(true);
  const recharger = useCallback(async () => {
    setChargement(true);
    try {
      setStations((await supplyApi.resupply()) || []);
    } catch {
      setStations([]);
    } finally {
      setChargement(false);
    }
  }, []);
  useEffect(() => {
    recharger();
  }, [recharger]);
  return { stations, chargement, recharger };
};

/** Propositions de réapprovisionnement (lot 035) : cuves sous 2 jours d'autonomie, regroupées par ville. */
export const usePropositionsReappro = () => {
  const [plan, setPlan] = useState({ proposals: [], groups: [], total_liters: 0 });
  const [chargement, setChargement] = useState(true);
  useEffect(() => {
    let annule = false;
    supplyApi
      .replenishment()
      .then((donnees) => {
        if (!annule) setPlan(donnees);
      })
      .catch(() => {
        if (!annule) setPlan({ proposals: [], groups: [], total_liters: 0 });
      })
      .finally(() => {
        if (!annule) setChargement(false);
      });
    return () => {
      annule = true;
    };
  }, []);
  return { plan, chargement };
};

export const ListePropositionsReappro = ({ groupes, limite }) => {
  const lignes = limite ? groupes.slice(0, limite) : groupes;
  return (
    <ul className="divide-y divide-border/60" data-testid="propositions-reappro">
      {lignes.map((groupe) => (
        <li key={groupe.city || 'sans-ville'} className="space-y-1 px-3 py-2 text-sm">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="font-medium text-foreground">
              {groupe.city || 'Ville non renseignée'}
              <span className="text-xs font-normal text-muted-foreground">
                {' · '}
                {groupe.station_count} station{groupe.station_count > 1 ? 's' : ''}
              </span>
            </p>
            <div className="flex flex-wrap items-center gap-2">
              {(groupe.by_product || []).map((produit) => (
                <Badge key={produit.product_name || 'produit'} variant="outline">
                  {produit.product_name} {formatLitres(produit.liters)}
                </Badge>
              ))}
              <Badge variant="warning">{formatLitres(groupe.total_liters)}</Badge>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            {(groupe.items || [])
              .map((ligne) => `${ligne.station_name} · ${ligne.product_name || ligne.tank_name} ${formatLitres(ligne.stock_liters)} (${jours(ligne.autonomy_days)}) → ${formatLitres(ligne.suggested_liters)}`)
              .join(' — ')}
          </p>
        </li>
      ))}
    </ul>
  );
};

export const ListeStationsAReapprovisionner = ({ stations, limite, actionPour }) => {
  const lignes = limite ? stations.slice(0, limite) : stations;
  return (
    <ul className="divide-y divide-border/60" data-testid="stations-a-reapprovisionner">
      {lignes.map((station) => (
        <li key={station.station_id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm">
          <div className="min-w-0">
            <p className="font-medium text-foreground">
              {station.station_name}
              <span className="text-xs font-normal text-muted-foreground"> · {libelle('station_mode', station.operating_mode)}</span>
            </p>
            <p className="text-xs text-muted-foreground">
              {(station.tanks || [])
                .map((cuve) => `${cuve.product_name || cuve.tank_name} ${formatLitres(cuve.stock_liters)} (${jours(cuve.autonomy_days)})`)
                .join(' · ') || 'Aucune cuve active'}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {actionPour ? actionPour(station) : null}
            <Badge variant={station.low ? 'warning' : 'muted'}>{jours(station.min_autonomy_days)}</Badge>
            {station.pending_orders ? (
              <Badge variant="info">
                {station.pending_orders} commande{station.pending_orders > 1 ? 's' : ''} · {formatLitres(station.pending_liters)}
              </Badge>
            ) : null}
            {station.open_receptions ? (
              <Badge variant="outline">
                {station.open_receptions} réception{station.open_receptions > 1 ? 's' : ''} attendue{station.open_receptions > 1 ? 's' : ''}
              </Badge>
            ) : null}
          </div>
        </li>
      ))}
    </ul>
  );
};

/**
 * Ligne de la répartition : rien pour une société sans station. Une seule ligne
 * quel que soit le nombre de stations — le détail (la liste des stations et ce
 * qu'il faut leur livrer) s'ouvre dans un volet, pour que la liste des commandes
 * reste ce qu'on regarde en premier.
 */
export const StationsToResupply = () => {
  const { stations } = useStationsAReapprovisionner();
  const { plan } = usePropositionsReappro();
  if (!stations.length) return null;
  const basses = stations.filter((station) => station.low).length;
  // Litres que le plan propose de livrer à chaque station (somme de ses cuves).
  const litresProposes = {};
  plan.proposals.forEach((ligne) => {
    litresProposes[ligne.station_id] = (litresProposes[ligne.station_id] || 0) + (Number(ligne.suggested_liters) || 0);
  });
  const motif = [
    basses ? `${basses} cuve${basses > 1 ? 's' : ''} presque vide${basses > 1 ? 's' : ''}` : 'stocks suffisants',
    plan.groups.length ? `${formatLitres(plan.total_liters)} à livrer` : null,
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <BandeauFile
      icone={Fuel}
      titre="Stations à réapprovisionner"
      nom="stations"
      nombre={stations.length}
      elements={stations}
      ton={basses ? 'warning' : 'muted'}
      motif={motif}
      testid="repartition-stations"
      aide={
        <>
          Autonomie : jours de vente que couvre le stock de la cuve, au rythme des 7 derniers jours vendus. Une
          commande « Réapprovisionnement de ma station » se répartit sur les camions comme une vente.
        </>
      }
      renderListe={(lignes, fermer) => (
        <>
          <ListeStationsAReapprovisionner
            stations={lignes}
            actionPour={(station) => {
              const litres = Math.round(litresProposes[station.station_id] || 0);
              const params = new URLSearchParams({ reappro: station.station_id });
              if (litres > 0) params.set('litres', String(litres));
              return (
                <Button asChild size="sm" variant={station.low ? 'default' : 'outline'} onClick={fermer}>
                  <Link to={`/orders?${params.toString()}`}>Commander</Link>
                </Button>
              );
            }}
          />
          <div className="px-3 pt-2">
            <Button asChild size="sm" variant="ghost" onClick={fermer}>
              <Link to="/stations/reapprovisionnement">Tout voir</Link>
            </Button>
          </div>
        </>
      )}
      pied={
        plan.groups.length ? (
          <div className="mt-2 border-t border-border/60" data-testid="repartition-propositions">
            <h3 className="flex items-center gap-1 px-3 pt-2 text-sm font-semibold text-foreground">
              À livrer
              <Badge variant="warning">{formatLitres(plan.total_liters)}</Badge>
              <Aide titre="À livrer">
                Cuves à moins de 2 jours de vente. Les litres proposés remplissent la cuve jusqu’à 90 % de sa capacité ;
                les stations d’une même ville se servent avec un même camion.
              </Aide>
            </h3>
            <ListePropositionsReappro groupes={plan.groups} />
          </div>
        ) : null
      }
    />
  );
};

export default StationsToResupply;
